import clsx from 'clsx'
import { Observer, useLocalObservable } from 'mobx-react'
import type { RefObject } from 'react'
import React, { useEffect, useRef } from 'react'

import type { StyleProps } from '@/types'

import { DropdownContext } from './context'
import S from './Dropdown.style'

export type DropdownCustomButtonProps = {
  value: any
  placeholder?: string
  contents?: React.ReactNode
  toggleFn: (e: React.MouseEvent) => void
  isOpen: boolean
  disabled?: boolean
  className?: StyleProps['className']
  ref: RefObject<HTMLButtonElement>
  hideArrow?: boolean
}

export type DropdownRenderProps = {
  CustomButton: (props: DropdownCustomButtonProps) => React.ReactNode
  onChange?: (value: any) => void
  value?: any
  placeholder?: string
  width?: number
  children?: React.ReactNode
  disabled?: boolean
  parentClassName?: StyleProps['className']
  buttonClassName?: StyleProps['className']
  hideArrow?: boolean
  id?: string
  onOpen?: () => void
} & StyleProps

const _DropdownRender = ({
  buttonClassName,
  onChange,
  width,
  className,
  value,
  placeholder,
  children,
  CustomButton,
  style,
  parentClassName,
  disabled,
  hideArrow,
  id,
  onOpen,
  ...props
}: DropdownRenderProps) => {
  const store = useLocalObservable(() => {
    return {
      selectedNode: undefined as undefined | React.ReactNode,
      isOpen: false,
      disabled,
      toggle() {
        if (this.disabled) return
        store.isOpen ? store.close() : store.open()
      },
      open() {
        store.isOpen = true
        onOpen?.()
        requestAnimationFrame(() => {
          if (listRef.current) {
            const scrollEl = listRef.current.querySelector<HTMLDivElement>('.dropdown-scroll-wrap')
            const activeEl = listRef.current.querySelector<HTMLButtonElement>('.active')

            if (scrollEl && activeEl) {
              const scrollActiveItemCenter =
                activeEl.offsetTop - scrollEl.offsetHeight / 2 + activeEl.offsetHeight / 2

              scrollEl.scrollTop = scrollActiveItemCenter
            }
          }
        })
      },
      close() {
        store.isOpen = false
      },
      onDropdownSelect(newValue: string | number, node: React.ReactNode, isOnChange = false) {
        if (value !== newValue) {
          value = newValue
          if (isOnChange) onChange?.(value)
        }
        if (node !== undefined) store.selectedNode = node

        store.close()
      },
      setScrollTop(v: number) {
        if (listRef.current) {
          // TODO: scroll 이동 넣기
          listRef.current.scrollTop = v
        }
      },
    }
  })

  const dropDownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event) {
      if (buttonRef.current && !buttonRef.current.contains(event.target)) {
        store.close()
      }
    }
    const popoverElement = parentClassName
      ? buttonRef.current?.closest(`.${parentClassName}`)
      : null

    document.addEventListener('click', handleClickOutside)
    popoverElement?.addEventListener('click', handleClickOutside)

    return () => {
      document.removeEventListener('click', handleClickOutside)
      popoverElement?.removeEventListener('click', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    store.disabled = !!disabled
  }, [disabled])

  useEffect(() => {
    store.onDropdownSelect(value, store.selectedNode, false)
  }, [value])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropDownRef.current && !dropDownRef.current.contains(event.target)) {
        store.close()
      }
    }

    document.addEventListener('click', handleClickOutside)

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [])

  return (
    <Observer>
      {() => (
        <DropdownContext.Provider
          value={{
            value,
            placeholder,
            onSelect: store.onDropdownSelect,
            isOpen: store.isOpen,
            setScrollTop: store.setScrollTop,
          }}
        >
          <div
            css={[S.basic.wrapper]}
            style={{ width: width ? `${width}px` : 'auto', ...style }}
            className={className}
            ref={dropDownRef}
            id={id}
            {...props}
          >
            {CustomButton({
              value,
              placeholder,
              contents: store.selectedNode,
              toggleFn: () => {
                store.toggle()
              },
              disabled,
              isOpen: store.isOpen,
              className: buttonClassName,
              ref: buttonRef, // TODO: 여기 warning나고 있음. forwardRef 써야함.
              hideArrow,
            })}
            <div className={clsx('__dropdown-list', store.isOpen && 'open')} ref={listRef}>
              {children}
            </div>
          </div>
        </DropdownContext.Provider>
      )}
    </Observer>
  )
}

_DropdownRender.displayName = 'DropdownRender'

const DropdownRender = _DropdownRender

export default DropdownRender
